import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { GOOGLE_REVIEWS_DATA } from '../data/servicesData';
import { Star, CheckCircle2, MessageSquare, Sparkles, MapPin, ArrowRight } from 'lucide-react';

export const GoogleReviewsSection = ({
  onOpenInquiry,
}) => {
  const [activeFilter, setActiveFilter] = useState('all');

  const filterCategories = [
    { id: 'all', label: 'All Reviews (5.0 ★)' },
    { id: 'subpaisa', label: 'Subpaisa & Gateways' },
    { id: 'payouts', label: 'Payouts & All Banks' },
    { id: 'validation', label: 'Validation Kit' },
    { id: 'loans', label: 'Loans & EMI Calculator' },
    { id: 'insurance', label: 'Insurance' },
  ];

  const filteredReviews = GOOGLE_REVIEWS_DATA.filter((item) => {
    if (activeFilter === 'all') return true;
    if (activeFilter === 'subpaisa') return item.solutionTag.toLowerCase().includes('subpaisa') || item.solutionTag.toLowerCase().includes('razorpay');
    if (activeFilter === 'payouts') return item.solutionTag.toLowerCase().includes('payout');
    if (activeFilter === 'validation') return item.solutionTag.toLowerCase().includes('validation');
    if (activeFilter === 'loans') return item.solutionTag.toLowerCase().includes('loan');
    if (activeFilter === 'insurance') return item.solutionTag.toLowerCase().includes('insurance');
    return true;
  });

  return (
    <section className="py-16 bg-white border-b border-slate-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-10">
        
        {/* Section Header with Authentic Google Rating Badge */}
        <div className="flex flex-col md:flex-row items-center justify-between gap-6 pb-6 border-b border-slate-200">
          <div className="space-y-2 text-center md:text-left">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-900 border border-amber-200 text-xs font-bold uppercase tracking-wider">
              <Sparkles className="w-3.5 h-3.5 text-amber-600" />
              <span>Verified Merchant Feedbacks</span>
            </div>

            <h2 className="text-2xl sm:text-3xl font-black text-[#0c2e6b] tracking-tight">
              Google Reviews &amp; Client Feedback
            </h2>

            <p className="text-xs sm:text-sm text-slate-600 max-w-xl">
              Real 5-star ratings from verified merchants, retail stores, logistics firms, and fintech partners across India.
            </p>
          </div>

          {/* Big Google Rating Summary Card */}
          <div className="p-4 sm:p-5 rounded-2xl bg-slate-50 border-2 border-slate-200 flex items-center gap-4 shadow-xs shrink-0">
            {/* Google "G" Badge */}
            <div className="w-12 h-12 rounded-xl bg-white border border-slate-200 shadow-xs flex items-center justify-center font-black text-2xl text-blue-600">
              G
            </div>

            <div>
              <div className="flex items-center gap-2">
                <span className="text-2xl font-black text-slate-900 font-mono">5.0</span>
                <div className="flex text-amber-400">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                  ))}
                </div>
              </div>
              <p className="text-[11px] font-bold text-slate-500 mt-0.5">
                Based on 340+ Verified Google Reviews
              </p>
            </div>
          </div>
        </div>

        {/* Filter Pills */}
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-none">
          {filterCategories.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveFilter(tab.id)}
              className={`px-3.5 py-1.5 rounded-xl text-xs font-bold whitespace-nowrap transition-all cursor-pointer ${
                activeFilter === tab.id
                  ? 'bg-blue-700 text-white shadow-xs'
                  : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* Reviews Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          <AnimatePresence mode="popLayout">
            {filteredReviews.map((rev) => (
              <motion.div
                key={rev.id}
                layout
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
                className="p-5 rounded-2xl bg-slate-50/80 border border-slate-200/90 hover:border-blue-300 hover:bg-white hover:shadow-md transition-all flex flex-col justify-between space-y-4"
              >
                <div className="space-y-3">
                  {/* Author Header */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-2.5">
                      <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-900 font-extrabold text-xs flex items-center justify-center border border-blue-200">
                        {rev.avatarText}
                      </div>
                      <div>
                        <h4 className="text-xs font-black text-slate-900">
                          {rev.authorName}
                        </h4>
                        <p className="text-[11px] text-slate-500 truncate max-w-[140px]">
                          {rev.companyOrRole}
                        </p>
                      </div>
                    </div>

                    <span className="text-[10px] text-slate-400 font-medium">
                      {rev.timeAgo}
                    </span>
                  </div>

                  {/* 5 Stars and Solution Tag */}
                  <div className="space-y-1">
                    <div className="flex items-center gap-1">
                      <div className="flex text-amber-400">
                        {[...Array(5)].map((_, i) => (
                          <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />
                        ))}
                      </div>
                      <span className="text-[11px] font-bold text-slate-700 ml-1">5.0</span>
                    </div>

                    <span className="inline-block px-2 py-0.5 rounded-md bg-blue-100/70 text-blue-900 text-[10px] font-bold">
                      {rev.solutionTag}
                    </span>
                  </div>

                  {/* Review Quote */}
                  <p className="text-xs text-slate-700 leading-relaxed italic">
                    &ldquo;{rev.reviewText}&rdquo;
                  </p>
                </div>

                {/* Footer badge with Location */}
                <div className="pt-2 border-t border-slate-200/80 flex items-center justify-between text-[11px] text-slate-500">
                  <span className="flex items-center gap-1">
                    <MapPin className="w-3 h-3 text-slate-400" />
                    <span>{rev.city}</span>
                  </span>

                  <span className="inline-flex items-center gap-1 text-emerald-700 font-bold">
                    <CheckCircle2 className="w-3 h-3 text-emerald-600" />
                    <span>{rev.verifiedBadge}</span>
                  </span>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>

        {/* Give Feedback Call to Action */}
        <div className="p-4 rounded-2xl bg-blue-50/70 border border-blue-200 flex flex-col sm:flex-row items-center justify-between gap-3 text-center sm:text-left">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-blue-700 text-white flex items-center justify-center shrink-0">
              <MessageSquare className="w-5 h-5" />
            </div>
            <div>
              <h4 className="text-xs sm:text-sm font-bold text-blue-950">
                Are you an existing merchant or partner?
              </h4>
              <p className="text-[11px] sm:text-xs text-slate-600">
                Submit your experience and feedback to our Hyderabad quality assurance team.
              </p>
            </div>
          </div>

          <button
            onClick={() => onOpenInquiry('Merchant Feedback')}
            className="px-4 py-2 rounded-xl bg-blue-700 hover:bg-blue-800 text-white font-bold text-xs uppercase tracking-wider transition-colors shrink-0 shadow-xs cursor-pointer flex items-center gap-1.5"
          >
            <span>Submit Feedback</span>
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>

      </div>
    </section>
  );
};
